import slugify from 'slugify';

type URLParams = Record<string, string | number>;

class URLBuilder {
    private patterns: Record<string, string> = {};
  
    constructor(urlPatterns: Record<string, string>) {
        this.patterns = urlPatterns;
    }
  
    build(patternName: string, params: URLParams = {}): string {
        const pattern = this.patterns[patternName];
        if (!pattern) {
            throw new Error(`URL pattern '${patternName}' not found`);
        }
    
        let url = pattern;
    
        // Replace :param with actual values
        for (const [key, value] of Object.entries(params)) {
            url = url.replace(`:${key}`, String(value));
        }
    
        // Handle optional parameters (marked with ?)
        // Remove optional parameters that weren't provided
        url = url.replace(/:(\w+)\?/g, '');
    
        // Check for remaining unresolved required parameters
        const unresolvedParams = url.match(/:(\w+)(?!\?)/g);
        if (unresolvedParams) {
            const missingParams = unresolvedParams.map(p => p.substring(1));
            throw new Error(`Missing required parameters: ${missingParams.join(', ')} for URL pattern '${patternName}'`);
        }
    
        return url;
    }
  
    getRequiredParams(patternName: string): string[] {
        const pattern = this.patterns[patternName];
        if (!pattern) {return [];}
    
        const matches = pattern.match(/:(\w+)/g);
        return matches ? matches.map(match => match.substring(1)) : [];
    }
}

// Global URL builder instance
let urlBuilder: URLBuilder | null = null;

export const initializeURLBuilder = (patterns: Record<string, string>) => {
    urlBuilder = new URLBuilder(patterns);
};

export const buildURL = (patternName: string, params: URLParams = {}): string => {
    if (!urlBuilder) {
        throw new Error('URL Builder not initialized. Call initializeURLBuilder first.');
    }
    return urlBuilder.build(patternName, params);
};

export const getRequiredParams = (patternName: string): string[] => {
    if (!urlBuilder) {return [];}
    return urlBuilder.getRequiredParams(patternName);
};

// Convenience functions for common patterns
export const buildEventURL = (patternName: string, eventId: string | number) => 
    buildURL(patternName, {event_id: eventId});

export const buildAgentURL = (patternName: string, agentId: string | number) => 
    buildURL(patternName, {agent_id: agentId});

export const buildStoryURL = (patternName: string, storyId: string | number) => 
    buildURL(patternName, {story_id: storyId});

export const buildUserURL = (patternName: string, userId: string | number) => 
    buildURL(patternName, {user_id: userId});

export const buildTeamURL = (patternName: string, teamId: string | number) => 
    buildURL(patternName, {team_id: teamId});

export const buildStoryboardURL = (patternName: string, storyboardId: string | number) => 
    buildURL(patternName, {storyboard_id: storyboardId});

export const buildNotificationURL = (patternName: string, notificationId: string | number) => 
    buildURL(patternName, {notification_id: notificationId});

export const buildCustomerURL = (patternName: string, customerId: string | number) => 
    buildURL(patternName, {customer_id: customerId});

// ============================================================================
// FRONTEND NAVIGATION URL HELPERS
// ============================================================================

// Event Detail URLs
export const buildEventDetailURL = (eventId: string | number) => 
    buildURL('eventDetailView', {event_id: eventId});

// Agent URLs
export const buildAgentDashboardURL = (agentId: string | number) => 
    buildURL('agentDashboardView', {agent_id: agentId});

export const buildAgentDetailURL = (agentId: string | number) => 
    buildURL('agentDetailView', {agent_id: agentId});

export const buildSpecialAgentStoryURL = (agentId: string | number) => 
    buildURL('agentDashboardView', {agent_id: agentId});

// Team URLs
export const buildTeamAnalyticsURL = (teamId: string | number) => 
    buildURL('teamAnalyticsView', {team_id: teamId});

export const buildTeamMembersURL = (teamId: string | number) => 
    buildURL('teamMembersView', {team_id: teamId});

export const buildTeamDetailURL = (teamId: string | number) => 
    buildURL('teamDetailView', {team_id: teamId});

export const buildConversationsEventsTeamURL = (teamId: string | number) => 
    buildURL('conversationsEventsTeamView', {team_id: teamId});

// Customer URLs
export const buildCustomerDashboardURL = (customerId: string | number) => 
    buildURL('customerDashboardView', {customer_id: customerId});

export const buildCustomerDetailURL = (customerId: string | number) => 
    buildURL('customerDetailView', {customer_id: customerId});

export const buildCustomerCallsURL = (customerId: string | number) => 
    buildURL('customerDetailCallsView', {customer_id: customerId});

export const buildSpecialCustomerStoryURL = (customerId: string | number) => 
    buildURL('customerDashboardView', {customer_id: customerId});

export const buildConversationsEventsCustomerURL = (customerId: string | number) => 
    buildURL('conversationsEventsCustomerView', {customer_id: customerId});

// Admin URLs
export const buildAdminChannelEvaluationTasksURL = (channelId: string | number) => 
    buildURL('adminChannelEvaluationTasksView', {channel_id: channelId});

export const buildAdminChannelRulesURL = (channelId: string | number) => 
    buildURL('adminChannelRulesView', {channel_id: channelId});

export const buildAdminChannelActionsURL = (channelId: string | number) => 
    buildURL('adminChannelActionsView', {channel_id: channelId});

export const buildAdminQualityAssuranceURL = (eventType: string, dateType: string) => 
    buildURL('adminQualityAssuranceView', {event_type: eventType, date_type: dateType});

// Story & Storyboard URLs
export const buildStoryDetailURL = (storyHashid: string) => 
    buildURL('storyDetailView', {story_hashid: storyHashid});

export const buildStoryTemplateDetailURL = (assignedUrl: string) => 
    buildURL('storyTemplateDetailView', {story_assigned_url: assignedUrl});

export const buildStoryStoryboardURL = (storyboardId: string | number, storyId: string | number) => 
    buildURL('storyStoryboardView', {storyboard_id: storyboardId, story_id: storyId});

export const buildAuditedStoryboardInsightsURL = (
    storyboardHashid: string,
    storyboardTitle: string | null | undefined,
    urlPathSegment: string | null | undefined,
): string => {
    const trimmedTitle = (storyboardTitle ?? '').trim();
    const fromTitle = trimmedTitle ? slugify(trimmedTitle, {lower: true}) : '';
    const rawSeg = (urlPathSegment ?? '').trim();
    const fromSeg = rawSeg
        ? rawSeg.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
        : '';
    const slug = fromTitle || fromSeg || 'storyboard';
    return buildStoryStoryboardURL(storyboardHashid, slug);
};

export const buildStoryboardTemplateURL = (assignedUrl: string, storyboardSlug: string) => 
    buildURL('storyboardTemplateView', {assigned_url: assignedUrl, storyboard_slug: storyboardSlug});

export const buildStoryboardsTemplateDetailURL = (assignedUrl: string, storyboardSlug: string, evaluationIdentifier: string) => 
    buildURL('storyboardsTemplateDetailView', { 
        assigned_url: assignedUrl, 
        storyboard_slug: storyboardSlug,
        evaluation_identifier: evaluationIdentifier 
    });

export const buildSpecialStoryDetailURL = (storySlug: string) => 
    buildURL('specialStoryDetailView', {story_slug: storySlug});

export const buildSpecialStoryObjectDetailURL = (assignedUrl: string, objId: string | number) => 
    buildURL('specialStoryObjectDetailView', {story_assigned_url: assignedUrl, obj_id: objId});

// Search URLs
export const buildSearchDashboardURL = () => 
    buildURL('searchDashboardView');

// Conversation URLs  
export const buildConversationsURL = () => 
    buildURL('conversationsView');

export const buildConversationsCallsURL = () => 
    buildURL('conversationsCallsView');

export const buildConversationsChatsURL = () => 
    buildURL('conversationsChatsView');

export const buildConversationsEmailsURL = () => 
    buildURL('conversationsEmailsView');

export const buildConversationsFeedbacksURL = () => 
    buildURL('conversationsFeedbacksView');

export const buildConversationsEventsAgentURL = (agentId: string | number) => 
    buildURL('conversationsEventsAgentView', {agent_id: agentId});

// Storyboard URLs for AgentDashboardView
export const buildStoryboardViewURL = (storyboardHashid: string, storyboardSlug: string) => 
    buildURL('storyboardView', {storyboard_id: storyboardHashid, story_id: storyboardSlug});


